import { createContext, useCallback, useContext, useMemo } from 'react'
import type { ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api'
import type { LoginPayload, RegisterPayload, User } from '@/api'
import { queryKeys } from '@/lib/queryKeys'

interface AuthContextValue {
  user: User | null
  isAuthenticated: boolean
  isAdmin: boolean
  /** true enquanto a sessão inicial ainda não foi resolvida. */
  isLoading: boolean
  login: (payload: LoginPayload) => Promise<User>
  register: (payload: RegisterPayload) => Promise<User>
  logout: () => Promise<void>
  /** Recarrega o usuário após uma edição no perfil. */
  refresh: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()

  const { data: user, isLoading } = useQuery({
    queryKey: queryKeys.session,
    queryFn: api.auth.getSession,
    // A sessão é lida uma vez por carga; mutações abaixo atualizam o cache.
    staleTime: 5 * 60 * 1000,
    retry: false,
  })

  const loginMutation = useMutation({ mutationFn: api.auth.login })
  const registerMutation = useMutation({ mutationFn: api.auth.register })

  const login = useCallback(
    async (payload: LoginPayload) => {
      const session = await loginMutation.mutateAsync(payload)
      queryClient.setQueryData(queryKeys.session, session.user)
      // A sacola e o perfil mudam de dono ao trocar de sessão.
      await queryClient.invalidateQueries({ queryKey: queryKeys.purchases })
      return session.user
    },
    [loginMutation, queryClient],
  )

  const register = useCallback(
    async (payload: RegisterPayload) => {
      const session = await registerMutation.mutateAsync(payload)
      queryClient.setQueryData(queryKeys.session, session.user)
      return session.user
    },
    [registerMutation, queryClient],
  )

  const logout = useCallback(async () => {
    await api.auth.logout()
    queryClient.setQueryData(queryKeys.session, null)
    queryClient.removeQueries({ queryKey: queryKeys.purchases })
  }, [queryClient])

  const refresh = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.session })
  }, [queryClient])

  const value = useMemo<AuthContextValue>(
    () => ({
      user: user ?? null,
      isAuthenticated: !!user,
      isAdmin: user?.role === 'admin',
      isLoading,
      login,
      register,
      logout,
      refresh,
    }),
    [user, isLoading, login, register, logout, refresh],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth precisa estar dentro de <AuthProvider>')
  return context
}
